// src/firebase.js
import { initializeApp, getApps } from "firebase/app";
import {
  getFirestore,
  collection,
  addDoc,
  enableMultiTabIndexedDbPersistence,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getFunctions } from "firebase/functions";
import { getStorage } from "firebase/storage";

// Production Firebase (used when not in dev or when dev env vars are unset)
const productionConfig = {
  apiKey: "AIzaSyD-v5Iyko9c5C8C8OF1glf9HWKp3gmATec",
  authDomain: "gladiators-website.firebaseapp.com",
  projectId: "gladiators-website",
  storageBucket: "gladiators-website.firebasestorage.app",
  messagingSenderId: "451350736495",
  appId: "1:451350736495:web:0c0eccf989249bdc07279a",
  measurementId: "G-G2W6CQ5MN4",
};

// Dev-only: use separate Firebase project when REACT_APP_FIREBASE_PROJECT_ID is set (e.g. in .env.development.local)
const isDev = process.env.NODE_ENV === "development";
const devProjectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
const useDevFirebase = isDev && devProjectId && process.env.REACT_APP_FIREBASE_API_KEY;

const devConfig = useDevFirebase
  ? {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FIREBASE_APP_ID,
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || undefined,
    }
  : null;

const firebaseConfig = useDevFirebase ? devConfig : productionConfig;

if (useDevFirebase) {
  console.info("[Firebase] Using DEV project:", firebaseConfig.projectId);
}

// Initialize (guard against re-init in hot reloads)
const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);

// Services
const db = getFirestore(app);
const auth = getAuth(app);
// Match deployed 1st-gen functions region (see Firebase console / deploy output).
const functions = getFunctions(app, "us-central1");
const storage = getStorage(app);

// --- Offline mode for Firestore ---
// Multi-tab persistence lets you keep multiple tabs open without errors.
// Writes queue while offline and sync automatically when back online.
//
// In development, persistence is skipped by default. IndexedDB + multi-tab
// sync often logs benign errors like "Failed to obtain primary lease for
// action 'Collect garbage'" under hot reload, React Strict Mode, or several
// localhost tabs competing for the lease. Production keeps persistence on.
// Set REACT_APP_FIRESTORE_PERSISTENCE_IN_DEV=true to enable persistence locally.
const useFirestorePersistenceInDev =
  process.env.REACT_APP_FIRESTORE_PERSISTENCE_IN_DEV === "true";
const shouldEnableFirestorePersistence =
  process.env.NODE_ENV === "production" || useFirestorePersistenceInDev;

if (shouldEnableFirestorePersistence) {
  enableMultiTabIndexedDbPersistence(db).catch((err) => {
    if (err?.code === "unimplemented") {
      console.warn("IndexedDB persistence not supported in this browser.");
    } else if (err?.code === "failed-precondition") {
      console.warn("Firestore persistence failed (another tab may control persistence).");
    } else {
      console.warn("Firestore persistence error:", err);
    }
  });
}

// Export everything you already use (unchanged API surface)
export { app, db, auth, functions, collection, addDoc, storage };
